import React, { useRef, useState, useEffect } from "react";
import { 
  MessageSquare, 
  Search, 
  ListFilter, 
  RotateCcw, 
  Globe,
  Download,
  Filter,
  Calendar,
  DollarSign
} from "lucide-react";
import SocialActivationModal from "./components/SocialActivationModal.jsx";
import SocialReceipt from "./components/SocialReceipt";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { getAgentSocialOrders } from "../../../services/socialProductsService";
import { handleSetSearch } from "../../../utils/searchDebouncing.js";
import ARHCustomDataTable from "../../../components/dataTable/ARHCustomDataTable.jsx";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

function SocialActivation() {
  const { t } = useTranslation();
  const [activationModalOpen, setActivationModalOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const searchRef = useRef(null);
  const userInfo = useSelector((state) => state.auth.user);
  const [refetch, setRefetch] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isDark, setIsDark] = useState(false);


  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const [filter, setFilter] = useState({
    search: "",
    limit: 10,
    page: 1,
    status: "",
    startDate: "",
    endDate: "",
    receiver: "",
    minAmount: "",
    maxAmount: "",
    sortBy: "createdAt",
    sortOrder: "DESC",
  });

  const closeActivationModal = () => setActivationModalOpen(false);

  const { data, isLoading, isError, error, refetch: refetchData } = useQuery({
    queryKey: ["socialOrders", filter, refetch],
    queryFn: () => getAgentSocialOrders(userInfo?.uid, filter),
  });

  const handleRefetch = () => {
    setRefetch(prev => !prev);
    toast.success(t('socialActivation.toasts.refreshSuccess'));
  };

  const handleActivationSuccess = () => {
    setActivationModalOpen(false);
    handleRefetch();
    toast.success(t('socialActivation.toasts.activationSuccess'));
  };

  const handleSearchChange = (value) => {
    setFilter(prev => ({
      ...prev,
      search: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilter(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setFilter(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilter(prev => ({ 
      ...prev, 
      status: status === "all" ? "" : status,
      page: 1 
    }));
  };

  const handleDateFilter = (start, end) => {
    setFilter(prev => ({
      ...prev,
      startDate: start,
      endDate: end,
      page: 1
    }));
  };

  const handleAmountFilter = (min, max) => {
    setFilter(prev => ({
      ...prev,
      minAmount: min,
      maxAmount: max,
      page: 1
    }));
  };

  const handleSort = (sortBy, sortOrder) => {
    setFilter(prev => ({
      ...prev,
      sortBy,
      sortOrder,
      page: 1
    }));
  };

  const handleClearFilters = () => {
    setFilter({
      search: "",
      limit: 10,
      page: 1,
      status: "",
      startDate: "",
      endDate: "",
      receiver: "",
      minAmount: "",
      maxAmount: "",
      sortBy: "createdAt",
      sortOrder: "DESC",
    });
    setShowFilters(false);
    toast.info(t('socialActivation.toasts.filtersCleared'));
  };

  const handleExportData = () => {
    toast.info(t('socialActivation.toasts.exportComing'));
  };

  const getPlatformIcon = (account) => {
    if (!account) return "📱";
    account = account.toLowerCase();
    if (account.includes('facebook') || account.includes('fb')) return '🔵';
    if (account.includes('instagram') || account.includes('ig')) return '🌈';
    if (account.includes('whatsapp') || account.includes('wa')) return '🟢';
    if (account.includes('twitter') || account.includes('x')) return '🐦';
    if (account.includes('tiktok')) return '🎵';
    if (account.includes('telegram') || account.includes('tg')) return '✈️';
    if (account.includes('snapchat')) return '👻';
    if (account.includes('youtube') || account.includes('yt')) return '▶️';
    return '📱';
  };

  const columns = [
    { 
      key: "txnNumber", 
      label: t('socialActivation.columns.transactionId'),
      sortable: true,
      render: (item) => (
        <div className={`font-mono text-sm ${isDark ? 'text-secondary-400' : 'text-secondary-600'}`}>
          {item?.txnNumber || "-"}
        </div>
      )
    },
    { 
      key: "receiver", 
      label: t('socialActivation.columns.socialAccount'),
      sortable: true,
      render: (item) => {
        return (
          <div className="flex items-center gap-2">
            <div>
              <div className={`font-medium truncate max-w-[150px] ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {item?.receiver || "-"}
              </div>
              {item?.operator && (
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {item.operator}
                </div>
              )}
            </div>
          </div>
        );
      }
    },
 
    { 
      key: "totalAmount", 
      label: t('socialActivation.columns.amount'),
      sortable: true,
      render: (item) => (
        <div className="font-semibold text-green-600 dark:text-green-400">
          {parseFloat(item?.totalAmount || item?.amount || 0).toFixed(2)} AFN
        </div>
      )
    },
    {
      key: "product",
      label: t('socialActivation.columns.product'),
      render: (item) => (
        <div className="max-w-[180px]">
          <div className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {item?.productDetails?.productName?.en || item?.productDetails?.productName || "-"}
          </div>
          {item?.productDetails?.description && (
            <div className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {item.productDetails.description}
            </div>
          )}
        </div>
      )
    },
    {
      key: "status",
      label: t('socialActivation.columns.status'),
      sortable: true,
      render: (item) => {
        const statusColors = {
          succeeded: { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-800 dark:text-green-300" },
          pending: { bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-800 dark:text-yellow-300" },
          processing: { bg: "bg-secondary-100 dark:bg-secondary-900/30", text: "text-secondary-800 dark:text-secondary-300" },
          failed: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-800 dark:text-red-300" },
          cancelled: { bg: "bg-gray-100 dark:bg-gray-700", text: "text-gray-800 dark:text-gray-300" }
        };
        
        const status = item?.status || 'pending';
        const colors = statusColors[status] || statusColors.pending;
        
        return (
          <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center justify-center w-fit min-w-[80px] ${colors.bg} ${colors.text}`}>
            {t(`socialActivation.status.${status}`)}
          </div>
        );
      }
    },
    {
      key: "createdAt",
      label: t('socialActivation.columns.createdAt'),
      sortable: true,
      render: (item) => (
        <div className="text-sm">
          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}
          </div>
          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {item?.createdAt ? new Date(item.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ""}
          </div>
        </div>
      )
    },
    {
      key: "actions",
      label: t('socialActivation.columns.actions'),
      render: (item) => (
        <div className="flex gap-2">
          <button 
            onClick={() => {
              setSelectedTransaction(item);
              setReceiptOpen(true);
            }}
            className={`text-sm px-3 py-1.5 border rounded transition-colors flex items-center gap-1 ${
              isDark 
                ? 'text-secondary-400 border-secondary-400 hover:bg-secondary-900/30' 
                : 'text-secondary-600 border-secondary-600 hover:bg-secondary-50'
            }`}
          >
            <MessageSquare size={14} />
            {t('socialActivation.actions.receipt')}
          </button>
          {item.status === 'pending' && (
            <button 
              onClick={() => {
                toast.info(t('socialActivation.toasts.cancelComing'));
              }}
              className={`text-sm px-3 py-1.5 border rounded transition-colors ${
                isDark 
                  ? 'text-red-400 border-red-400 hover:bg-red-900/30' 
                  : 'text-red-600 border-red-600 hover:bg-red-50'
              }`}
            >
              {t('socialActivation.actions.cancel')}
            </button>
          )}
        </div>
      )
    }
  ];

  const customEmptyState = (
    <div className="w-full min-h-[300px] flex flex-col items-center justify-center py-10">
      <div className="relative">
        <div className={`w-40 h-40 flex items-center justify-center rounded-full ${
          isDark 
            ? 'bg-gradient-to-br from-secondary-900/30 to-primary-900/30' 
            : 'bg-gradient-to-br from-secondary-50 to-primary-50'
        }`}>
          <MessageSquare size={64} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
        </div>
        <div className={`absolute -bottom-2 -right-2 w-12 h-12 rounded-full flex items-center justify-center ${
          isDark ? 'bg-secondary-900/30' : 'bg-secondary-100'
        }`}>
          <Globe size={20} className={isDark ? 'text-secondary-400' : 'text-secondary-600'} />
        </div>
      </div>
      <p className={`mt-6 text-lg font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
        {t('socialActivation.emptyState.title')}
      </p>
      <p className={`mt-2 text-center max-w-md ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {t('socialActivation.emptyState.description')}
      </p>
      <button
        onClick={() => setActivationModalOpen(true)}
        className="mt-6 px-6 py-3 bg-gradient-to-r from-secondary-600 to-primary-600 text-white rounded-lg hover:from-secondary-700 hover:to-primary-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2"
      >
        <MessageSquare size={18} />
        {t('socialActivation.emptyState.button')}
      </button>
    </div>
  );

  const customErrorState = (
    <div className="w-full min-h-[300px] flex flex-col items-center justify-center py-10">
      <div className={`w-32 h-32 flex items-center justify-center rounded-full ${
        isDark ? 'bg-red-900/30' : 'bg-red-50'
      }`}>
        <MessageSquare size={48} className={isDark ? 'text-red-400' : 'text-red-400'} />
      </div>
      <p className={`mt-6 text-lg font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
        {t('socialActivation.errorState.title')}
      </p>
      <div className={`mt-2 text-sm mb-4 text-center max-w-md ${isDark ? 'text-red-400' : 'text-red-600'}`}>
        {error?.message || t('socialActivation.errorState.message')}
      </div>
      <div className="flex gap-3">
        <button
          onClick={handleRefetch}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
        >
          <RotateCcw size={16} />
          {t('socialActivation.errorState.retry')}
        </button>
        <button
          onClick={() => window.location.reload()}
          className={`px-4 py-2 border rounded transition-colors ${
            isDark 
              ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {t('socialActivation.errorState.refreshPage')}
        </button>
      </div>
    </div>
  );

  const stats = data?.data?.pagination || {
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    itemsPerPage: 10
  };

  const calculateStats = () => {
    const orders = data?.data?.orders || [];
    const succeeded = orders.filter(o => o.status === 'succeeded').length;
    const pending = orders.filter(o => o.status === 'pending').length;
    const totalAmount = orders.reduce((sum, o) => sum + parseFloat(o.totalAmount || 0), 0);
    const avgAmount = orders.length > 0 ? totalAmount / orders.length : 0;

    return {
      total: orders.length,
      succeeded,
      pending,
      totalAmount: totalAmount.toFixed(2),
      avgAmount: avgAmount.toFixed(2)
    };
  };

  const summaryStats = calculateStats();

  return (
    <div className={`w-full px-5 py-4 min-h-screen transition-colors duration-300 ${
      isDark ? 'bg-gray-900' : 'bg-white'
    }`}>
      <SocialActivationModal 
        open={activationModalOpen} 
        onClose={closeActivationModal} 
        onSuccess={handleActivationSuccess} 
      />
      
      <SocialReceipt
        transaction={selectedTransaction} 
        isOpen={receiptOpen} 
        onClose={() => setReceiptOpen(false)} 
      />
      
      <div className="w-full">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold transition-colors duration-300 ${
              isDark ? 'text-white' : 'text-gray-800'
            }`}>
              {t('socialActivation.title')}
            </h1>
            <p className={`transition-colors duration-300 ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {t('socialActivation.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <div className={`bg-gradient-to-r from-secondary-50 to-secondary-100 border border-secondary-200 rounded-lg p-4 ${
          isDark ? 'from-secondary-900/20 to-secondary-800/20 border-secondary-800' : ''
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-secondary-300' : 'text-secondary-700'}`}>
                {t('socialActivation.stats.totalActivations')}
              </p>
              <p className={`text-2xl font-bold ${isDark ? 'text-secondary-200' : 'text-secondary-900'}`}>
                {summaryStats.total}
              </p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isDark ? 'bg-secondary-800' : 'bg-secondary-200'
            }`}>
              <MessageSquare size={24} className={isDark ? 'text-secondary-300' : 'text-secondary-600'} />
            </div>
          </div>
        </div>

        <div className={`bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4 ${
          isDark ? 'from-green-900/20 to-green-800/20 border-green-800' : ''
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                {t('socialActivation.stats.successful')}
              </p>
              <p className={`text-2xl font-bold ${isDark ? 'text-green-200' : 'text-green-900'}`}>
                {summaryStats.succeeded}
              </p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isDark ? 'bg-green-800' : 'bg-green-200'
            }`}>
              <MessageSquare size={24} className={isDark ? 'text-green-300' : 'text-green-600'} />
            </div>
          </div>
        </div>

        <div className={`bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-4 ${
          isDark ? 'from-yellow-900/20 to-yellow-800/20 border-yellow-800' : ''
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                {t('socialActivation.stats.pending')}
              </p>
              <p className={`text-2xl font-bold ${isDark ? 'text-yellow-200' : 'text-yellow-900'}`}>
                {summaryStats.pending}
              </p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isDark ? 'bg-yellow-800' : 'bg-yellow-200'
            }`}>
              <MessageSquare size={24} className={isDark ? 'text-yellow-300' : 'text-yellow-600'} />
            </div>
          </div>
        </div>

        <div className={`bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-4 ${
          isDark ? 'from-primary-900/20 to-primary-800/20 border-primary-800' : ''
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-primary-300' : 'text-primary-700'}`}>
                {t('socialActivation.stats.totalAmount')}
              </p>
              <p className={`text-2xl font-bold ${isDark ? 'text-primary-200' : 'text-primary-900'}`}>
                {summaryStats.totalAmount} AFN
              </p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isDark ? 'bg-primary-800' : 'bg-primary-200'
            }`}>
              <DollarSign size={24} className={isDark ? 'text-primary-300' : 'text-primary-600'} />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-5 mt-6">
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className={`max-w-[400px] w-[500px] h-[44px] relative rounded-md border ${
            isDark 
              ? 'border-gray-600 bg-gray-800' 
              : 'border-[#E4E7EC] bg-white'
          }`}>
            <input
              onChange={(e) => {
                handleSetSearch(handleSearchChange, e.target.value, searchRef);
              }}
              type="text"
              placeholder={t('socialActivation.search.placeholder')}
              defaultValue={filter.search}
              className={`bg-transparent h-full pl-10 pr-3 py-2 w-full outline-none transition-colors duration-300 ${
                isDark ? 'text-white placeholder-gray-400' : 'text-gray-900'
              }`}
            />
            <Search size={18} className={`absolute top-[12px] left-[8px] ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`} />
            {filter.search && (
              <button
                onClick={() => handleSearchChange("")}
                className="absolute top-[12px] right-[8px] text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
              >
                ×
              </button>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-md text-sm flex items-center gap-2 whitespace-nowrap transition-colors ${
              isDark 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter size={16} />
            {t('socialActivation.filters.title')}
            {Object.values(filter).filter(v => v && v !== "createdAt" && v !== "DESC").length > 2 && (
              <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {Object.values(filter).filter(v => v && v !== "createdAt" && v !== "DESC").length - 2}
              </span>
            )}
          </button>
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={handleRefetch}
            className={`px-4 py-2 border rounded-md text-sm flex items-center gap-2 transition-colors ${
              isDark 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <RotateCcw size={16} />
            {t('socialActivation.buttons.refresh')}
          </button>
          <button
            onClick={() => setActivationModalOpen(true)}
            className="bg-gradient-to-r from-secondary-600 to-primary-600 px-4 py-2 rounded-md text-white text-sm hover:from-secondary-700 hover:to-primary-700 flex items-center gap-2 transition-all"
          >
            <MessageSquare size={16} />
            {t('socialActivation.buttons.activate')}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className={`mt-4 p-4 border rounded-lg transition-colors duration-300 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {t('socialActivation.filters.advancedFilters')}
            </h3>
            <button
              onClick={handleClearFilters}
              className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              {t('socialActivation.filters.clearAll')}
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t('socialActivation.filters.status')}
              </label>
              <select
                value={filter.status}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className={`w-full border rounded-md px-3 py-2 text-sm transition-colors ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">{t('socialActivation.filters.allStatus')}</option>
                <option value="succeeded">{t('socialActivation.status.succeeded')}</option>
                <option value="pending">{t('socialActivation.status.pending')}</option>
                <option value="processing">{t('socialActivation.status.processing')}</option>
                <option value="failed">{t('socialActivation.status.failed')}</option>
                <option value="cancelled">{t('socialActivation.status.cancelled')}</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t('socialActivation.filters.dateRange')}
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filter.startDate}
                  onChange={(e) => handleDateFilter(e.target.value, filter.endDate)}
                  className={`w-full border rounded-md px-3 py-2 text-sm transition-colors ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <input
                  type="date"
                  value={filter.endDate}
                  onChange={(e) => handleDateFilter(filter.startDate, e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 text-sm transition-colors ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <ARHCustomDataTable
          data={data?.data?.orders || []}
          columns={columns}
          meta={stats}
          loading={isLoading}
          isError={isError}
          error={error}
          onRetry={handleRefetch}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          searchQuery={filter.search}
          emptyState={customEmptyState}
          errorState={customErrorState}
          rowClassName={`hover:bg-gray-50 transition-colors ${isDark ? 'dark:hover:bg-gray-700' : ''}`}
          headerClassName={isDark ? 'bg-gray-800' : 'bg-gray-50'}
          showPagination={true}
          showEntries={true}
          showSearch={false}
        />
      </div>
    </div>
  );
}

export default SocialActivation;